import http from '@/biz/http.mjs'
import extendNode from '../extendNode.mjs'
import parseMap from '../../scripts/parseMap.mjs'
import Color from '../Color.mjs'
import useComponentNode from '../_COMPONENT/useComponentNode.jsx'
import api from './apiBdrule.mjs'
import meta from './metaBdrule.mjs'
import Model from './ModelBdrule.mjs'
import RuleType from './RuleType.jsx'
import PropertiesPane from './PropertiesPaneBdrule.jsx'
import PropertiesPaneBatch from './PropertiesPaneBatchBdrule.jsx'
import FormChoose from './FormChooseBdrule.jsx'
import TableChoose from './TableChooseBdrule.jsx'

const ruleTypes = new Map(
    RuleType.map(([type, name, icon]) => [type, {icon, name, type}])
)

export default () => {
    const ComponentNode = useComponentNode()

    return extendNode(ComponentNode, {
        ...meta,
        api,
        FormChoose,
        TableChoose,
        Model,
        PropertiesPane,
        PropertiesPaneBatch,

        attrNodes: {
            top: ['ATTR_BDRULE_RESUME'],
        },

        canGrow(map, node) {
            const {mapTypeCode} = map.data

            if ('DA_REFERENCE_MAP' === mapTypeCode) {
                return true
            }
            else {
                return ComponentNode.canGrow.call(this, map, node)
            }
        },

        canMountType(map, node, type) {
            return (
                /^(AR|DOC|PIC|URL)$/.test(type) ||
                ComponentNode.canMountType.call(this, map, node, type)
            )
        },

        getDesc(map, node) {
            const {ruleType} = node.data

            if (! ruleType || 'COMMON' === ruleType) {
                return this.typeName(map, node.parent)
            }

            const {name = ''} = ruleTypes.get(ruleType) ?? {}
            return name
        },

        getIcons(map, node) {
            const {ruleType} = node.data
            const {icon} = ruleTypes.get(ruleType) ?? {}

            if (icon) {
                return [icon]
            }
            else {
                return []
            }
        },

        menuItemsInsertCommon(map, node) {
            return [
                {type: 'AR'},
                {type: 'PIC'},
                {type: 'DOC'},
                {type: 'URL'},
            ]
        },

        async onDoubleClick(map, node, event) {
            const {mapTypeCode} = map.data

            if ('DA_REFERENCE_MAP' !== mapTypeCode) {
                return ComponentNode.onDoubleClick.call(this, map, node, event)
            }
        },

        async _atOwnerPush(map, node, event) {
            const d = await this._getPushData(map, node)
            event.detail.ruleList.push(d)
        },

        _getCategories(map, node) {
            return [
                'BDRULE_DF',
                'BDRULE_DT',
            ]
        },

        _getDefaultDataFields(map, node) {
            return {
                ...ComponentNode._getDefaultDataFields.call(this, map, node),
                ruleType: 'COMMON'
            }
        },

        _getShape(map, node) {
            return 'EllipseRectangle'
        },

        _getThemeColor(map, node) {
            const {ruleType} = node.data

            if (ruleType && 'COMMON' !== ruleType) {
                return Color.CYAN
            }
            else {
                return Color.LIGHT_YELLOW
            }
        },

        async _onChange(map, node, event) {
            await ComponentNode._onChange.call(this, map, node, event)

            if (event.target === node) {
                const oldData = event.detail

                if (node.data.ruleType !== oldData.ruleType) {
                    map.forceRerenderChildren(node)
                }
            }
        },

        async _readGrowTree(map, node) {
            const {mapTypeCode, prjPatternId} = map.data

            if ('DA_REFERENCE_MAP' === mapTypeCode) {
                const {pkid, prjId, sVer} = node.data

                const result = await http.get(
                    '/llmDtGenDo/extendDo',
                    {pkid, prjId, sVer}
                )

                const children = parseMap(result)
                return {children}
            }
            else {
                const readImplementer = prjPatternId ?
                    async () => {
                        const {rev, ruleCode} = node.data

                        const result = await http.get(
                            '/patterns/rule/extend',
                            {prjPatternId, rev, ruleCode}
                        )

                        return result && parseMap(result)
                    }
                    :
                    async () => {
                        const {prjId, rev, ruleCode, sVer} = node.data

                        const result = await http.get(
                            '/dmbds/rule/extend',
                            {prjId, rev, ruleCode, sVer}
                        )

                        return result && parseMap(result)
                    }

                const [tree, implementer] = await Promise.all([
                    ComponentNode._readGrowTree.call(this, map, node),
                    readImplementer(),
                ])

                if (implementer) {
                    tree.children.unshift(implementer)
                }

                if (2 !== tree.children.length) {
                    return tree
                }

                const [n0, n1] = tree.children

                if (! (
                    'BDRULE_DF' === n0.data.bizNodeType &&
                    'BDRULE_DT' === n1.data.bizNodeType
                )) {
                    return tree
                }

                if (0 < n1.children.length) {
                    return {...tree, children: n1.children}
                }
                else {
                    return {...tree, children: n0.children}
                }
            }
        },
    })
}
